import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { AlignBottomIcon } from '@/components/tiptap-editor/tiptap-icons/align-bottom-icon';
import { AlignTopIcon } from '@/components/tiptap-editor/tiptap-icons/align-top-icon';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { moveNode } from '@/hooks/use-move-node';
import { useSiblingNodeInfo } from '@/hooks/use-sibling-node-info';
import { dispatchMetaTransaction } from '@/lib/zy-tiptap-utils';
import { type ReactNodeViewProps, NodeViewContent } from '@tiptap/react';
import { CopyPlusIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { memo } from 'react';

const ADD_CHOICE_CLASS =
    'group flex items-center justify-center gap-2 rounded-lg border-2 border-gray-300 px-4 py-1 text-gray-500 transition-all duration-200 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none dark:border-gray-600 dark:text-gray-400 dark:hover:border-blue-500 dark:hover:bg-blue-950/20 dark:hover:text-blue-400 dark:focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed';

const MultipleChoiceNodeView: React.FC<ReactNodeViewProps> = ({ node, selected, editor, getPos, deleteNode, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);

    const addChoice = () => {
        const pos = getPos();
        if (!pos) {
            return;
        }

        const newPos = pos + node.nodeSize - 1;

        editor
            .chain()
            .focus()
            .insertContentAt(newPos, {
                type: 'multipleChoiceChoice',
                attrs: {
                    isCorrect: false,
                },
                content: [
                    {
                        type: 'paragraph',
                    },
                    {
                        type: 'multipleChoiceExplanation',
                        content: [
                            {
                                type: 'paragraph',
                            },
                        ],
                    },
                ],
            })
            .selectDescendantNodeContent('multipleChoiceChoice', newPos)
            .run();

        dispatchMetaTransaction(editor, { multipleChoiceChoiceUpdate: true });
    };

    const { nodeIndex, totalNodes } = useSiblingNodeInfo({
        editor,
        getPos,
        nodeTypeName: 'multipleChoice',
    });

    const handleMoveUp = () => {
        if (nodeIndex === 0) return;
        const pos = getPos();
        if (typeof pos !== 'number') return;
        moveNode(editor, 'up', { pos, node });
    };

    const handleMoveDown = () => {
        const pos = getPos();
        if (typeof pos !== 'number') return;
        moveNode(editor, 'down', { pos, node });
    };

    const handleDelete = () => {
        if (totalNodes <= 1) return;
        if (window.confirm('Are you sure you want to delete this question?')) {
            deleteNode();
        }
    };

    const handleDuplicate = () => {
        if (window.confirm('Are you sure you want to duplicate this question?')) {
            const pos = getPos();
            if (typeof pos !== 'number') return;
            editor
                .chain()
                .duplicateNode(node, pos + node.nodeSize)
                .scrollIntoView()
                .run();
        }
    };

    const isDeleteDisabled = () => {
        return totalNodes <= 1;
    };

    const isMoveUpDisabled = () => {
        return nodeIndex === 0 || totalNodes <= 1;
    };

    const isMoveDownDisabled = () => {
        return nodeIndex === totalNodes - 1 || totalNodes <= 1;
    };

    const getMoveQuestionTooltip = () => {
        if (totalNodes <= 1) return 'Only one question in the MCQ set';
        if (nodeIndex === 0) return 'This is the first question in the MCQ set';
        if (nodeIndex === totalNodes - 1) return 'This is the last question in the MCQ set';
        return '';
    };

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label={`Question ${nodeIndex + 1}`}
            selected={selected}
            containerClassName="rounded-lg border-2 border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50"
            contentClassName="pt-3 px-1"
        >
            <div className="flex min-w-0 gap-1">
                <div contentEditable={false}>
                    <div className="flex flex-col gap-2 ">
                        <Button
                            onClick={handleMoveUp}
                            data-style="ghost"
                            aria-label="Move question up"
                            disabled={isMoveUpDisabled() || isEditorReadonly}
                            tooltip={isMoveUpDisabled() ? getMoveQuestionTooltip() : 'Move question up'}
                        >
                            <AlignTopIcon className="h-4 w-4" />
                        </Button>
                        <Button
                            onClick={handleMoveDown}
                            data-style="ghost"
                            aria-label="Move question down"
                            disabled={isMoveDownDisabled() || isEditorReadonly}
                            tooltip={isMoveDownDisabled() ? getMoveQuestionTooltip() : 'Move question down'}
                        >
                            <AlignBottomIcon className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
                <div className="min-w-0 w-full">
                    <NodeViewContent />
                    <div
                        className="mb-2 flex gap-2 justify-end"
                        contentEditable={false}
                    >
                        <button
                            onClick={addChoice}
                            className={ADD_CHOICE_CLASS}
                            disabled={isEditorReadonly}
                        >
                            <PlusIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">Add Choice</span>
                        </button>
                    </div>
                </div>
                <div className="flex flex-col">
                    <Button
                        onClick={handleDelete}
                        data-style="ghost"
                        aria-label="Delete question"
                        disabled={isDeleteDisabled() || isEditorReadonly}
                        tooltip={isDeleteDisabled() ? 'An MCQ set must have at least one question' : 'Delete question'}
                    >
                        <Trash2Icon className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={handleDuplicate}
                        data-style="ghost"
                        aria-label="Duplicate question"
                        disabled={isEditorReadonly}
                        tooltip="Duplicate question"
                    >
                        <CopyPlusIcon className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </CommonNodeViewWrapper>
    );
};

export default memo(MultipleChoiceNodeView);
